/**
 * Input de recherche avec debounce intégré.
 *
 * Pourquoi un composant dédié ? Parce que tous les listings (companies,
 * brokers, projects, contacts) ont besoin du même pattern :
 *   - input contrôlé local
 *   - propage la valeur après un délai (300 ms)
 *   - reset pagination si la query change
 *
 * Volontairement pas de useDebounce hook séparé : la logique est trop
 * simple et locale pour mériter un fichier dédié. Si un autre call site
 * apparaît, on extraira.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  /** Valeur courante du store parent (debounced). Sert d'init/reset. */
  value: string;
  onChange: (value: string) => void;
  /** Délai debounce ms. Default 300. */
  delay?: number;
  placeholder?: string;
};

export function SearchInput({ value, onChange, delay = 300, placeholder }: Props) {
  const { t } = useTranslation();
  const [local, setLocal] = useState(value);
  const lastEmitted = useRef(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync externe → interne (ex. reset depuis le parent).
  useEffect(() => {
    if (value !== lastEmitted.current) {
      setLocal(value);
      lastEmitted.current = value;
    }
  }, [value]);

  // Debounce interne → externe.
  useEffect(() => {
    if (local === lastEmitted.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      lastEmitted.current = local;
      onChange(local);
    }, delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [local, delay, onChange]);

  return (
    <input
      type="search"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      placeholder={placeholder ?? t("common.search")}
      className="input"
      aria-label={placeholder ?? t("common.search")}
    />
  );
}
